import type { ButtonBlockAbstract, CollectEvent, RichTextContentProtocol } from '@lighthouse/core'
import { EVENT_VARIABLE_TYPE } from '@lighthouse/core'
import { ActionButton, flexMinSizeStyle, useMultipleFloatBoxBlockContext, useUnifiedTap } from '@lighthouse/shared'
import React, { memo, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { AnimationDecorators } from '../../components'
import { useEffectiveAnchorId } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'

interface ButtonBlockProps extends React.ComponentPropsWithoutRef<'div'> {
    blockData: ButtonBlockAbstract
    loading?: boolean
    stopActionPropagation?: boolean
    onClickEvent?: (ev: React.MouseEvent | React.TouchEvent) => Promise<void> | undefined
    onRenderTitle: (v: RichTextContentProtocol) => string
    onEventTrigger?: (
        collectEvents: CollectEvent[] | undefined,
        type: EVENT_VARIABLE_TYPE,
        option?: { pageName?: string; blockName?: string }
    ) => void
}

const SCxContainer = styled(AnimationDecorators)<Pick<React.CSSProperties, 'justifyContent'>>`
    width: 100%;
    padding: var(--block-padding);
`

const ButtonBlock: React.FC<ButtonBlockProps> = ({ blockData, loading, stopActionPropagation, onClickEvent, onRenderTitle, onEventTrigger }) => {
    const { config, id, title } = blockData
    const { radius = '0', size, collectEvents, breakPoint, cursor, anchor, ...restConfig } = config
    const { interactionProps, reference } = useMultipleFloatBoxBlockContext()
    const actionConfig = useMemo(() => ({ id, ...restConfig }), [id, restConfig])
    const { anchorId } = anchor || {}
    const effectiveAnchorId = useEffectiveAnchorId(anchorId)
    useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)
    const handleClickAction = useCallback(
        (ev: React.MouseEvent | React.TouchEvent) => {
            onClickEvent?.(ev)
            onEventTrigger?.(collectEvents, EVENT_VARIABLE_TYPE.BUTTON_CLICK, { blockName: title })
        },
        [collectEvents, onClickEvent, onEventTrigger, title]
    )

    const unifiedTap = useUnifiedTap(handleClickAction)

    return (
        <SCxContainer blockId={id} blockName={title} animation={breakPoint.animation} id={effectiveAnchorId}>
            <ActionButton
                styles={{
                    container: {
                        ...flexMinSizeStyle,
                        width: '100%'
                    }
                }}
                ref={ref => reference(ref as unknown as HTMLDivElement)}
                size={size}
                cursor={cursor}
                radius={restConfig.shape === 'rectangle' ? radius : '100'}
                config={actionConfig}
                loading={loading}
                // disabled={loading}
                // 只有会消费点击的 trigger 才阻止上层动作；hover-only 浮层不应挡住普通点击。
                data-stop-action-propagation={stopActionPropagation ? true : undefined}
                {...interactionProps.getReferenceProps(unifiedTap)}
                onRenderTitle={onRenderTitle}
            />
        </SCxContainer>
    )
}

export default memo(ButtonBlock)
